import { Box, Chip, Stack, Typography } from '@mui/material';
import EastRoundedIcon from '@mui/icons-material/EastRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import { alpha, useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import type { ReactNode } from 'react';
import { ApplicationSectionCard } from './applicationSectionChrome';
import type { IdentitySummaryTone } from '../../lib/identitySessionSummary';
import type {
  IdentityContactChannelRow,
  IdentityContactChoiceInsight,
  IdentityPostMatchFact,
  IdentityPostMatchInsights,
} from '../../lib/identityPostMatchInsights';

function toneChipColor(
  tone: IdentitySummaryTone | undefined,
): 'default' | 'success' | 'warning' | 'error' | 'info' {
  if (tone === 'success' || tone === 'warning' || tone === 'error' || tone === 'info') return tone;
  return 'default';
}

function toneMain(tone: IdentitySummaryTone | undefined, theme: Theme) {
  const key = toneChipColor(tone);
  return key === 'default' ? theme.palette.text.secondary : theme.palette[key].main;
}

function displayContact(value: string | null): string {
  if (!value?.trim()) return '—';
  return value.trim();
}

function ContactChoiceRows({ choice }: { choice: IdentityContactChoiceInsight }) {
  const theme = useTheme();
  const decision = choice.decision;

  if (choice.channels.length === 0) return null;

  return (
    <Stack
      spacing={0.85}
      sx={{
        px: 1.1,
        py: 0.85,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor:
          theme.palette.mode === 'light'
            ? alpha(theme.palette.common.black, 0.02)
            : alpha(theme.palette.common.white, 0.03),
      }}
    >
      {choice.channels.map((row) => (
        <ChannelChoiceRow key={row.channel} row={row} decision={decision} />
      ))}
      {choice.note ? (
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.35 }}>
          {choice.note}
        </Typography>
      ) : null}
    </Stack>
  );
}

function ChannelChoiceRow({
  row,
  decision,
}: {
  row: IdentityContactChannelRow;
  decision: IdentityContactChoiceInsight['decision'];
}) {
  const label = row.channel === 'email' ? 'Email' : 'Phone';
  const onAccount = displayContact(row.onAccount);
  const fromApp = displayContact(row.fromThisApplication);
  const showAbandonedDraft =
    decision === 'kept_existing' &&
    Boolean(row.fromThisApplication) &&
    !row.sameAsAccount;
  const showUpdateBridge =
    decision === 'updated_to_new' &&
    !row.sameAsAccount &&
    Boolean(row.fromThisApplication || row.onAccount);
  const showUpdateUnchanged =
    decision === 'updated_to_new' && row.sameAsAccount && Boolean(row.onAccount || row.fromThisApplication);

  return (
    <Stack spacing={0.35} sx={{ minWidth: 0 }}>
      <Typography
        variant="caption"
        sx={{
          fontWeight: 800,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'text.secondary',
          fontSize: '0.62rem',
        }}
      >
        {label}
      </Typography>

      {decision === 'kept_existing' || (decision === 'pending' && row.sameAsAccount) ? (
        <Stack spacing={0.35}>
          <Stack direction="row" spacing={0.65} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 600, display: 'block' }}
            >
              On matched account
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, fontSize: '0.8125rem', wordBreak: 'break-word', lineHeight: 1.35 }}
            >
              {onAccount}
            </Typography>
            <Chip
              size="small"
              label={decision === 'kept_existing' ? 'Kept' : 'On file'}
              color={decision === 'kept_existing' ? 'success' : 'default'}
              variant="outlined"
              sx={{ height: 18, fontWeight: 700, fontSize: '0.62rem', '& .MuiChip-label': { px: 0.65 } }}
            />
          </Stack>
          {showAbandonedDraft ? (
            <Stack direction="row" spacing={0.65} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Started this application with
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  wordBreak: 'break-word',
                  lineHeight: 1.35,
                  color: 'text.secondary',
                  textDecoration: 'line-through',
                }}
              >
                {fromApp}
              </Typography>
              <Chip
                size="small"
                label="Not applied"
                variant="outlined"
                sx={{ height: 18, fontWeight: 700, fontSize: '0.62rem', '& .MuiChip-label': { px: 0.65 } }}
              />
            </Stack>
          ) : null}
        </Stack>
      ) : showUpdateBridge ? (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ minWidth: 0 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
              Was on account
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontSize: '0.8125rem',
                wordBreak: 'break-word',
                lineHeight: 1.35,
                color: 'text.secondary',
                textDecoration: 'line-through',
              }}
            >
              {onAccount}
            </Typography>
          </Box>
          <EastRoundedIcon sx={{ fontSize: 14, color: 'warning.main', flexShrink: 0, opacity: 0.8 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
              Updated to
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                fontSize: '0.8125rem',
                wordBreak: 'break-word',
                lineHeight: 1.35,
                color: 'warning.main',
              }}
            >
              {fromApp !== '—' ? fromApp : onAccount}
            </Typography>
          </Box>
        </Stack>
      ) : showUpdateUnchanged ? (
        <Stack direction="row" spacing={0.65} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            Signed-in account
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontWeight: 700, fontSize: '0.8125rem', wordBreak: 'break-word', lineHeight: 1.35 }}
          >
            {onAccount !== '—' ? onAccount : fromApp}
          </Typography>
          <Chip
            size="small"
            label="Already same"
            variant="outlined"
            sx={{ height: 18, fontWeight: 700, fontSize: '0.62rem', '& .MuiChip-label': { px: 0.65 } }}
          />
        </Stack>
      ) : (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ minWidth: 0 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
              On matched account
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, fontSize: '0.8125rem', wordBreak: 'break-word', lineHeight: 1.35 }}
            >
              {onAccount}
            </Typography>
          </Box>
          <EastRoundedIcon sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block' }}>
              From this application
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, fontSize: '0.8125rem', wordBreak: 'break-word', lineHeight: 1.35 }}
            >
              {fromApp}
            </Typography>
          </Box>
        </Stack>
      )}
    </Stack>
  );
}

function StepRow({
  index,
  title,
  fact,
  children,
  delay,
}: {
  index: string;
  title: string;
  fact: IdentityPostMatchFact;
  children?: ReactNode;
  delay: number;
}) {
  const theme = useTheme();
  const accent = toneMain(fact.tone, theme);

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.24, delay, ease: 'easeOut' }}
      sx={{
        position: 'relative',
        px: 1.35,
        py: 1.1,
        bgcolor:
          theme.palette.mode === 'light' ? alpha(accent, 0.05) : alpha(accent, 0.1),
        boxShadow: `inset 3px 0 0 ${alpha(accent, 0.9)}`,
        '&:hover': {
          bgcolor: alpha(accent, theme.palette.mode === 'light' ? 0.08 : 0.14),
        },
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 0.5, sm: 1 }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        flexWrap="wrap"
        useFlexGap
        sx={{ mb: fact.detail || children ? 0.55 : 0 }}
      >
        <Typography
          variant="caption"
          sx={{
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'text.secondary',
            fontSize: '0.62rem',
          }}
        >
          {index} · {title}
        </Typography>
        <Chip
          size="small"
          color={toneChipColor(fact.tone)}
          variant={fact.tone === 'success' ? 'filled' : 'outlined'}
          label={fact.value}
          sx={{ height: 22, fontWeight: 700 }}
        />
      </Stack>

      {fact.detail ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.4, fontSize: '0.8125rem', maxWidth: 720 }}
        >
          {fact.detail}
        </Typography>
      ) : null}

      {children ? <Box sx={{ mt: 0.75 }}>{children}</Box> : null}
    </Box>
  );
}

/**
 * Compact follow-through after a matched profile — stacked steps, not equal-height columns.
 */
export function IdentityPostMatchInsightsPanels({
  insights,
}: {
  insights: IdentityPostMatchInsights;
}) {
  if (!insights.accountContinuity.visible && !insights.returningFlow.visible) return null;

  const { signedIn, contacts, contactChoice } = insights.accountContinuity;
  const returning = insights.returningFlow;

  const stepCount =
    (insights.accountContinuity.visible ? 2 : 0) + (returning.visible ? 1 : 0);

  const metaBits: string[] = [];
  if (returning.visible) {
    if (returning.selfieSummary) {
      metaBits.push(
        returning.selfieDistance
          ? `${returning.selfieSummary} (distance ${returning.selfieDistance})`
          : returning.selfieSummary,
      );
    }
    if (returning.attemptStatus) metaBits.push(`Session ${returning.attemptStatus}`);
    if (returning.ccid) metaBits.push(returning.ccid);
  }

  return (
    <ApplicationSectionCard
      title="Follow-through"
      subtitle="After the match — sign-in, keep vs update contacts, then returning checks"
      icon={<TimelineRoundedIcon fontSize="small" />}
      endAdornment={
        <Chip
          size="small"
          variant="outlined"
          label={`${stepCount} step${stepCount === 1 ? '' : 's'}`}
          sx={{ height: 22, fontWeight: 700 }}
        />
      }
    >
      <Box
        sx={{
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          bgcolor: (t) =>
            t.palette.mode === 'light'
              ? alpha(t.palette.common.black, 0.01)
              : alpha(t.palette.common.white, 0.02),
        }}
      >
        <Stack divider={<Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />}>
          <StepRow index="01" title="Sign-in" fact={signedIn} delay={0.03} />

          <StepRow index="02" title="Contacts" fact={contacts} delay={0.06}>
            <ContactChoiceRows choice={contactChoice} />
          </StepRow>

          {returning.visible ? (
            <StepRow index="03" title="Returning" fact={returning.outcome} delay={0.09}>
              {metaBits.length > 0 ? (
                <Stack direction="row" flexWrap="wrap" gap={0.5} useFlexGap>
                  {metaBits.map((bit) => (
                    <Chip
                      key={bit}
                      size="small"
                      variant="outlined"
                      label={bit}
                      sx={{
                        height: 20,
                        maxWidth: '100%',
                        fontWeight: 600,
                        fontSize: '0.65rem',
                        '& .MuiChip-label': {
                          px: 0.75,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        },
                        ...(bit.startsWith('CCID_')
                          ? {
                              fontFamily:
                                'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                            }
                          : {}),
                      }}
                    />
                  ))}
                </Stack>
              ) : null}
            </StepRow>
          ) : null}
        </Stack>
      </Box>
    </ApplicationSectionCard>
  );
}
